import { Modal, ModalContent, ModalOverlay, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";
import { DISPARO_TEMPLATE_QUERY_KEY, DISPARO_TEMPLATES_QUERY_KEY } from "@/hooks/useDisparos";
import {
  CatalogoDisparo,
  CriarDisparoTemplateDto,
  DisparoTemplateItem,
} from "@/models/consignado/disparo.model";
import { atualizarDisparoTemplate } from "@/services/consignado/disparo.service";
import TemplateScreen from "./TemplateScreen";

type Props = {
  template: DisparoTemplateItem;
  catalogo?: CatalogoDisparo;
  isOpen: boolean;
  onClose: () => void;
  /** Template como ficou no servidor — o disparo reaplica a mensagem dele. */
  onSalvo: (template: DisparoTemplateItem) => void;
};

/**
 * Edita o template vinculado sem sair do disparo. É o mesmo editor da seção
 * Templates (TemplateScreen), com a mesma confirmação de que a mudança vale
 * para todos os disparos que usam o template.
 *
 * Esc e clique fora não fecham: a saída passa pelos botões do editor, que
 * perguntam antes de descartar alteração.
 */
export default function TemplateEditorJanela({ template, catalogo, isOpen, onClose, onSalvo }: Props) {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const queryClient = useQueryClient();

  const salvar = useMutation({
    mutationFn: (dto: CriarDisparoTemplateDto) => atualizarDisparoTemplate(template.id, dto),
    onSuccess: (salvo) => {
      toast({ title: "Template atualizado.", status: "success", duration: 3000, isClosable: true });
      queryClient.invalidateQueries({ queryKey: [DISPARO_TEMPLATES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DISPARO_TEMPLATE_QUERY_KEY, template.id] });
      onSalvo(salvo);
      onClose();
    },
    onError: toastErro("Não foi possível salvar o template.", "Confira os campos e tente de novo."),
  });

  return (
    // Sem foco automático: o foco da abertura marcava a referência de
    // "alterado" antes de o editor montar o rascunho, e o Cancelar perguntava
    // "Sair sem salvar?" num template que ninguém tinha tocado.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      closeOnEsc={false}
      closeOnOverlayClick={false}
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent overflowY="auto" bg="white">
        {isOpen && (
          <TemplateScreen
            mode="edit"
            template={template}
            catalogo={catalogo}
            isSaving={salvar.isPending}
            onBack={onClose}
            onSave={(dto) => salvar.mutateAsync(dto).catch(() => undefined)}
            emJanela
          />
        )}
      </ModalContent>
    </Modal>
  );
}
